import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import BottomNav from "../components/BottomNav";
import { MessageCircleHeart, Sparkles, ScrollText, HeartCrack, Heart, TrendingUp, Shield } from "lucide-react";

export default function Home() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ analyses: 0, matches: 0 });

  useEffect(() => {
    (async () => {
      try {
        const [a, m] = await Promise.all([
          api.get("/analyses").catch(() => ({ data: [] })),
          api.get("/matches").catch(() => ({ data: [] })),
        ]);
        setStats({ analyses: a.data.length, matches: m.data.length });
      } catch {}
    })();
  }, []);

  if (!user) return null;

  return (
    <div className="app-shell bg-aurora min-h-screen pb-nav" data-testid="home-page">
      {/* Header */}
      <div className="px-6 pt-12 pb-6">
        <p className="text-xs uppercase tracking-[0.18em] text-[#5F7F77] font-medium">Sentra AI ❤️</p>
        <h1 className="font-display text-4xl text-[#1A2924] mt-1 leading-tight">Ahoj, {user.name}</h1>
        <p className="text-[#54635F] text-sm mt-1.5">Co potřebuješ pochopit dnes?</p>
      </div>

      {/* Hero CTA - Analyzovat konverzaci */}
      <div className="px-6">
        <Link to="/analyze" data-testid="home-cta-analyze">
          <div className="relative rounded-3xl overflow-hidden p-7 bg-gradient-to-br from-[#082A24] via-[#0F3D34] to-[#FF7A00] text-white shadow-[0_20px_50px_-15px_rgba(15,61,52,0.5)] active:scale-[0.98] transition">
            <div className="absolute -top-10 -right-10 w-44 h-44 rounded-full bg-white/10 blur-2xl" />
            <div className="absolute -bottom-12 -left-8 w-40 h-40 rounded-full bg-[#C9A66B]/15 blur-2xl" />
            <div className="relative">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-[0.7rem] uppercase tracking-widest mb-4">
                <Sparkles size={12} /> Hlavní funkce
              </div>
              <h2 className="font-display text-3xl leading-tight mb-2">Analyzovat<br />konverzaci</h2>
              <p className="text-white/80 text-sm leading-relaxed mb-5 max-w-[18rem]">
                Nahraj WhatsApp, Messenger nebo SMS. AI ti vysvětlí, co se v komunikaci děje, upozorní na red flags a navrhne odpověď.
              </p>
              <div className="inline-flex items-center gap-2 text-sm font-medium">
                Začít analýzu <span className="text-lg">→</span>
              </div>
            </div>
          </div>
        </Link>
      </div>

      {/* Quick stats */}
      <div className="px-6 mt-4 grid grid-cols-2 gap-3">
        <Link to="/analyses" data-testid="home-history" className="glass-card p-4 active:scale-[0.97] transition">
          <ScrollText size={20} className="text-[#C9A66B] mb-2" strokeWidth={1.6} />
          <p className="text-2xl font-display text-[#1A2924]">{stats.analyses}</p>
          <p className="text-xs text-[#5F7F77] uppercase tracking-wider mt-0.5">Mých analýz</p>
        </Link>
        <Link to="/matches" data-testid="home-matches" className="glass-card p-4 active:scale-[0.97] transition">
          <Heart size={20} className="text-[#C9A66B] mb-2" strokeWidth={1.6} />
          <p className="text-2xl font-display text-[#1A2924]">{stats.matches}</p>
          <p className="text-xs text-[#5F7F77] uppercase tracking-wider mt-0.5">Matchů</p>
        </Link>
      </div>

      {/* Other features */}
      <div className="px-6 mt-6">
        <p className="text-xs uppercase tracking-[0.18em] text-[#5F7F77] font-medium mb-3">Další možnosti</p>
        <div className="space-y-3">
          <FeatureCard
            to="/discover"
            testid="home-discover"
            icon={<MessageCircleHeart size={20} className="text-[#C9A66B]" strokeWidth={1.6} />}
            title="Seznamka s AI kompatibilitou"
            subtitle="Najdi lidi, kteří se k tobě hodí podle hodnot a očekávání"
          />
          <FeatureCard
            to="/safedate"
            testid="home-safedate"
            icon={<Shield size={20} className="text-[#0F3D34]" strokeWidth={1.6} />}
            title="Safe Date 🛡️"
            subtitle="Randi bezpečně — kontakt, SOS, falešný hovor, sdílení polohy"
          />
          <FeatureCard
            to="/timeline"
            testid="home-timeline"
            icon={<TrendingUp size={20} className="text-[#0F3D34]" strokeWidth={1.6} />}
            title="Red Flag Timeline"
            subtitle="Vývoj kompatibility v čase z více analýz se stejnou osobou"
          />
          <FeatureCard
            to="/breakup"
            testid="home-breakup"
            icon={<HeartCrack size={20} className="text-[#C9A66B]" strokeWidth={1.6} />}
            title="Po rozchodu"
            subtitle="No-contact tracker, deník uzdravení, analýza zpráv od ex"
          />
        </div>
      </div>

      <BottomNav />
    </div>
  );
}

function FeatureCard({ to, testid, icon, title, subtitle, badge }) {
  const Wrap = badge ? "div" : Link;
  const wrapProps = badge ? { "data-testid": testid } : { to, "data-testid": testid };
  return (
    <Wrap {...wrapProps} className="glass-card flex items-center gap-4 p-4 active:scale-[0.98] transition block">
      <div className="w-11 h-11 rounded-2xl bg-[#FFE4CC] flex items-center justify-center shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="font-display text-lg text-[#1A2924] leading-tight">{title}</p>
        <p className="text-xs text-[#5F7F77] mt-0.5 leading-relaxed">{subtitle}</p>
      </div>
      {badge && <span className="pill text-[0.65rem]">{badge}</span>}
    </Wrap>
  );
}
